import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Plus, Trash2, Edit, Shield, Lock } from 'lucide-react';
import AppFooter from '../components/AppFooter';
import BackButton from '../components/BackButton';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = "https://customer-assets.emergentagent.com/job_yolo-crop-counter/artifacts/dqftlupc_Recurso%2021VR%20logos.png";

const ROLE_LABELS = { super_admin: 'Super Admin', admin: 'Administrador', operator: 'Operador', cashier: 'Cajero', auditor: 'Auditor' };
const ROLE_COLORS = { super_admin: 'bg-purple-600', admin: 'bg-blue-600', operator: 'bg-emerald-600', cashier: 'bg-amber-600', auditor: 'bg-slate-600' };

export default function UsersPage() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [form, setForm] = useState({ email: '', password: '', name: '', role: 'operator' });

  const fetchUsers = async () => {
    try {
      const { data } = await axios.get(`${API}/auth/users`, { withCredentials: true });
      setUsers(data);
    } catch {} finally { setIsLoading(false); }
  };

  useEffect(() => { fetchUsers(); }, []);

  const handleSave = async () => {
    try {
      if (editingUser) {
        await axios.put(`${API}/auth/users/${editingUser.id}`, form, { withCredentials: true });
        toast.success('Usuario actualizado');
      } else {
        await axios.post(`${API}/auth/users`, form, { withCredentials: true });
        toast.success('Usuario creado');
      }
      setShowModal(false);
      setEditingUser(null);
      setForm({ email: '', password: '', name: '', role: 'operator' });
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (userId) => {
    if (!window.confirm('Seguro que queres eliminar este usuario?')) return;
    try {
      await axios.delete(`${API}/auth/users/${userId}`, { withCredentials: true });
      toast.success('Usuario eliminado');
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error');
    }
  };

  const openEdit = (u) => {
    setEditingUser(u);
    setForm({ email: u.email, password: '', name: u.name, role: u.role });
    setShowModal(true);
  };

  const openNew = () => {
    setEditingUser(null);
    setForm({ email: '', password: '', name: '', role: 'operator' });
    setShowModal(true);
  };

  const availableRoles = user?.role === 'super_admin'
    ? ['super_admin', 'admin', 'operator', 'cashier', 'auditor']
    : ['admin', 'operator', 'cashier', 'auditor'];

  return (
    <div className="min-h-screen bg-slate-900 flex flex-col">
      <header className="bg-slate-800 border-b border-slate-700">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <BackButton to="/dashboard" />
              <div className="border-l border-slate-700 pl-4">
                <h1 className="text-lg font-bold text-white">Gestion de Usuarios</h1>
                <p className="text-sm text-slate-400">{users.length} usuarios</p>
              </div>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <Link to="/permissions">
                <Button variant="outline" className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 hover:text-emerald-200" data-testid="permissions-link"><Lock className="w-4 h-4 mr-2" />Permisos</Button>
              </Link>
              <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700" data-testid="add-user-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Usuario</Button>
              <img src={LOGO_URL} alt="ParkingVision AI" className="h-8 w-auto hidden sm:block" />
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex-1">
        {isLoading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : (
          <div className="space-y-3">
            {users.map((u) => (
              <Card key={u.id} className="bg-slate-800 border-slate-700">
                <CardContent className="p-4 flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${ROLE_COLORS[u.role] || 'bg-slate-600'}`}>
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{u.name}</p>
                      <p className="text-slate-400 text-sm">{u.email}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <Badge className={`${ROLE_COLORS[u.role] || 'bg-slate-600'}`}>{ROLE_LABELS[u.role] || u.role}</Badge>
                    {u.id !== user?.id && (
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => openEdit(u)} className="text-slate-400 hover:text-white" data-testid={`edit-user-${u.id}`}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(u.id)} className="text-slate-400 hover:text-red-400" data-testid={`delete-user-${u.id}`}><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    )}
                    {u.id === user?.id && <Badge variant="outline" className="text-slate-500 border-slate-600">Tu</Badge>}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      <AppFooter />

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white">
          <DialogHeader><DialogTitle>{editingUser ? 'Editar Usuario' : 'Nuevo Usuario'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label className="text-slate-300">Nombre</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} className="bg-slate-700 border-slate-600 text-white" data-testid="user-name-input" /></div>
            <div><Label className="text-slate-300">Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({...form, email: e.target.value})} className="bg-slate-700 border-slate-600 text-white" data-testid="user-email-input" /></div>
            <div><Label className="text-slate-300">{editingUser ? 'Nueva contrasena (dejar vacio para no cambiar)' : 'Contrasena'}</Label><Input type="password" value={form.password} onChange={(e) => setForm({...form, password: e.target.value})} className="bg-slate-700 border-slate-600 text-white" data-testid="user-password-input" /></div>
            <div>
              <Label className="text-slate-300">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({...form, role: v})}>
                <SelectTrigger className="bg-slate-700 border-slate-600 text-white" data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700">
                  {availableRoles.map(r => <SelectItem key={r} value={r}>{ROLE_LABELS[r]}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} className="border-slate-600 text-slate-300">Cancelar</Button>
            <Button onClick={handleSave} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
